import { useState, useEffect, useCallback } from "react";
import {
  fetchRcnDeliveries,
  invalidateRcnDeliveriesCache,
} from "api/rcnDeliveries";

/**
 * Hook to fetch and cache RCN deliveries.
 * Uses a 2-minute cache to avoid repeated API calls.
 * @returns {{ dispatches: Array, loading: boolean, error: string|null, refetch: function, invalidate: function }}
 */
export function useRcnDeliveries() {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRcnDeliveries(forceRefresh);
      setDispatches(data);
    } catch (err) {
      setError(err.message || "Failed to load");
      setDispatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const refetch = useCallback(() => load(true), [load]);
  const invalidate = useCallback(() => {
    invalidateRcnDeliveriesCache();
    load(true);
  }, [load]);

  return {
    dispatches,
    loading,
    error,
    refetch: () => load(true),
    invalidate,
  };
}
